import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'
import autoBind from 'react-autobind'

// Actions
import * as fromSelection from '../../stores/ducks/selection'
import * as fromProjects from '../../stores/ducks/projects'
import * as fromScenes from '../../stores/ducks/scenes'
import * as fromCombinations from '../../stores/ducks/combinations'
import * as fromPatterns from '../../stores/ducks/patterns/json'
import * as fromDesigns from '../../stores/ducks/designs'
import * as fromAppearanceGroups from '../../stores/ducks/appearance-groups'
import * as fromMaterialSearch from '../../stores/ducks/material-search'
import * as fromMaterials from '../../stores/ducks/materials'
import { getScene } from '../../stores/ducks/scenes'
import { clearDirty } from '../../stores/ducks/dirty'
import navigate from '../../utils/navigate'

// Selectors
import * as fromCombinationsSelectors from '../../stores/ducks/combinations/selectors'
import { getSavedSceneId } from '../../stores/ducks/scenes/selectors'
import { getIsDirty } from '../../stores/ducks/dirty/selectors'

// JSX Components : Layout
import Wrapper from '../layout/wrapper'
import Main from '../layout/main'
import Modal from '../common/modal'
import PanelGrid from '../common/panels/panel-grid'
import { CreatePatternModal } from './create-pattern-modal'

// JSX Components : Internal
import FileProgress from './file-progress'
import PanelLeft from './panels/panel-left'
import PanelRight from './panels/panel-right'
import Viewer from './viewer'
import Toolbar from './toolbar/toolbar'
import ImagePackageToolbar from './toolbar/toolbar-image-package'
import Header from './Header'
import NotificationBubble from '../common/notification-bubble'
import ConfirmLeaveDialog from './confirm-leave-dialog'
import SaveDialog from './panels/saved/save-dialog'
import { PROJECT_TYPES, TOP_BAR_HEIGHT } from '../../constants'

const LEAVE_DIALOG = { UNSAVED_CHANGES: 'unsaved_changes', SAVE_AS_NEW: 'save_as_new' }

/** To be able to rewrite this component to typescript we need to first
 * rewrite Header(layout), PanelLeft and PanelRight to typescript.
*/
class Visualizer extends PureComponent {
  constructor (props) {
    super(props)
    autoBind(this)
    this.state = { leaveState: null, deferredNavigationCallback: null }
  }

  componentDidMount () {
    document.body.classList.add('hide-scrollbar')
    window.removeEventListener('popstate', navigate.onPopstate)
    window.addEventListener('popstate', this.handlePopstate, false)
    this.props.setCurrent()
    this.props.fetchPatterns()
    this.props.getMaterialCategoriesList()
    this.props.fetchCarriers()
  }

  componentWillUnmount () {
    window.addEventListener('popstate', navigate.onPopstate, false)
    window.removeEventListener('popstate', this.handlePopstate)
    document.body.classList.remove('hide-scrollbar')
    this.props.clear()
    this.props.clearAppearanceGroupState()
    this.props.resetResourceFilters({ resource: 'variant' })
    this.props.clearAppearanceCategorieFilters()
    this.props.clearMarkedAppearanceAndColor()
    this.props.clearQueryString()
    this.props.clearScene()
  }

  handlePopstate (event) {
    if (!this.props.isDirty) {
      this.props.goBack()
      return
    }
    event.preventDefault()
    window.history.go(1)
    if (!this.props.savedSceneId) {
      this.setState({ leaveState: LEAVE_DIALOG.SAVE_AS_NEW, deferredNavigationCallback: this.props.goBack })
    } else {
      this.props.updateSavedScene(this.props.savedSceneId)
      this.props.goBack()
    }
  }

  openLeaveModal (deferredNavigationCallback) {
    if (this.props.isDirty) {
      this.setState({ leaveState: LEAVE_DIALOG.UNSAVED_CHANGES, deferredNavigationCallback })
    } else {
      deferredNavigationCallback()
    }
  }

  handleDiscard () {
    const { deferredNavigationCallback } = this.state
    this.setState({ leaveState: null, deferredNavigationCallback: null }, deferredNavigationCallback)
  }

  handleSave () {
    if (this.props.savedSceneId) {
      this.props.updateSavedScene(this.props.savedSceneId)
      this.state.deferredNavigationCallback()
    } else {
      this.setState({ leaveState: LEAVE_DIALOG.SAVE_AS_NEW })
    }
  }

  handleCloseModal () {
    this.setState({ leaveState: null, deferredNavigationCallback: null })
  }

  render () {
    const {
      goBack,
      imagePackageId,
      projectType,
      isMaster,
      projectId,
      leftPanelIndex,
      rightPanelIndex,
      combinationId
    } = this.props
    const { leaveState } = this.state

    return (
      <Wrapper>
        <Header onBeforeNavigate={this.openLeaveModal}>
          {projectType === PROJECT_TYPES.IMAGE_PACKAGE && (
            <ImagePackageToolbar isMaster={isMaster} />
          )}

          {projectType !== PROJECT_TYPES.IMAGE_PACKAGE && (
            <Toolbar />
          )}
        </Header>
        <Main>
          <div className='custom-scrollbar'>
            <PanelGrid panelWidth={TOP_BAR_HEIGHT}>
              <PanelLeft
                defaultIndex={leftPanelIndex}
                onBeforeNavigate={this.openLeaveModal}
                projectType={projectType}
                isMaster={isMaster}
              />
              <Viewer
                projectId={projectId}
                projectType={projectType}
                imagePackageId={imagePackageId}
                combinationId={combinationId}
              />
              <PanelRight
                defaultIndex={rightPanelIndex}
                projectType={projectType}
                isMaster={isMaster}
              />
            </PanelGrid>
          </div>
        </Main>
        <FileProgress
          goBack={goBack}
        />
        <CreatePatternModal />
        <NotificationBubble
          notification={this.props.notificationMessage}
          error={this.props.error}
          onClearOrTimeout={() => {
            this.props.resetNotificationMessage()
          }}
        />
        <Modal
          isOpen={leaveState !== null}
          onRequestClose={this.handleCloseModal}
          width={600}
        >
          {leaveState === LEAVE_DIALOG.UNSAVED_CHANGES && this.props.isDirty && (
            <ConfirmLeaveDialog
              onSave={this.handleSave}
              onDiscard={this.handleDiscard}
              onClose={this.handleCloseModal}
            />
          )}
          {leaveState === LEAVE_DIALOG.SAVE_AS_NEW && (
            <SaveDialog
              onSave={this.handleCloseModal}
              onClose={this.handleCloseModal}
            />
          )}
        </Modal>
      </Wrapper>
    )
  }
}

const mapStateToProps = createSelector(
  fromCombinationsSelectors.getCurrentEntry,
  state => state.projects.error,
  state => state.projects.notificationMessage,
  getIsDirty,
  getSavedSceneId,
  (_, ownProps) => ownProps,
  (combination, error, notificationMessage, isDirty, savedSceneId, ownProps) => {
    const isMaster = combination.isMaster
    const isNewImagePackage = isMaster && !combination.parentId
    const isEmptyScene = !ownProps.combinationId
    let leftPanelIndex = null

    if (isEmptyScene) {
      leftPanelIndex = 2
    }

    if (isNewImagePackage) {
      leftPanelIndex = 0
    }

    let rightPanelIndex = null

    if (combination.imageTemplateId) {
      rightPanelIndex = 1
    }

    return {
      leftPanelIndex,
      rightPanelIndex,
      projectType: ownProps.projectType,
      imagePackageId: combination.imagePackageId,
      isMaster: combination.isMaster || false,
      error: error && typeof error === 'object' ? JSON.stringify(error) : error,
      notificationMessage,
      combinationId: ownProps.combinationId,
      isDirty,
      savedSceneId
    }
  }
)

const mapDispatchToProps = (dispatch, ownProps) => ({
  setCurrent () {
    dispatch(fromScenes.setTempTitle({ title: null }))
    if (ownProps.sceneId) {
      dispatch(getScene(ownProps.sceneId, true))
    }
    dispatch(fromProjects.setCurrent(ownProps.projectId))
    // TODO is there any way to figure out if all project data has been fetched already? On reload, combination + render data disappears
    // TODO maybe check for access to project somewhere here?
    dispatch(fromProjects.getProject(ownProps.projectId, { mergeCombinations: true, force: true }))
    dispatch(fromCombinations.setCurrent(ownProps.combinationId))
  },
  clear () {
    dispatch(fromSelection.clear())
  },
  resetNotificationMessage: () => dispatch(fromProjects.setNotificationMessage(null)),
  fetchPatterns: () => dispatch(fromPatterns.getAvailablePatterns()),
  resetResourceFilters: (payload) => dispatch(fromDesigns.resetResourceFilters(payload)),
  clearAppearanceGroupState: () => dispatch(fromAppearanceGroups.clearAppearanceGroupsState()),
  getMaterialCategoriesList: () => dispatch(fromMaterialSearch.getMaterialCategoriesList()),
  clearAppearanceCategorieFilters: () => dispatch(fromMaterialSearch.clearAppearanceCategorieFilters()),
  clearQueryString: () => dispatch(fromMaterialSearch.setQueryString('')),
  clearMarkedAppearanceAndColor () {
    dispatch(fromProjects.clearMarkedAppearances(ownProps.projectId))
    dispatch(fromProjects.clearMarkedColors(ownProps.projectId))
  },
  fetchCarriers: () => dispatch(fromMaterials.fetchCarriers()),
  updateSavedScene: (savedSceneId) => dispatch(fromScenes.saveOrUpdateScene(savedSceneId)),
  clearScene: () => {
    dispatch(clearDirty())
    dispatch(fromScenes.setSaveSceneId(null))
    dispatch(fromScenes.setTempTitle({ title: null }))
  }
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Visualizer)
